import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Clock, User, Briefcase, Stethoscope, GraduationCap, Video, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDistanceToNow } from 'date-fns';

interface Draft {
  id: string;
  name: string;
  agent_type: string;
  platform: string;
  current_step: number;
  updated_at: string;
  expires_at: string;
}

interface DraftCardProps {
  draft: Draft;
  onDelete: (id: string) => void;
}

const TOTAL_STEPS = 4;

const typeConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  patient: { icon: <User className="w-4 h-4" />, label: 'Patient', color: 'bg-primary/10 text-primary' },
  customer: { icon: <Briefcase className="w-4 h-4" />, label: 'Customer', color: 'bg-accent/10 text-accent' },
  examiner: { icon: <Stethoscope className="w-4 h-4" />, label: 'Examiner', color: 'bg-orange-100 text-orange-700' },
  coach: { icon: <GraduationCap className="w-4 h-4" />, label: 'Coach', color: 'bg-purple-100 text-purple-700' },
};

export function DraftCard({ draft, onDelete }: DraftCardProps) {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleContinue = () => {
    navigate(`/admin/agents/create?draft=${draft.id}`);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await onDelete(draft.id);
    setIsDeleting(false);
  };

  const progressPercent = Math.round((draft.current_step / TOTAL_STEPS) * 100);
  const typeInfo = typeConfig[draft.agent_type] || typeConfig.patient;
  const lastUpdated = formatDistanceToNow(new Date(draft.updated_at), { addSuffix: true });
  const expiresIn = formatDistanceToNow(new Date(draft.expires_at), { addSuffix: false });

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground truncate">
              {draft.name || 'Untitled Draft'}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className={typeInfo.color}>
                {typeInfo.icon}
                <span className="ml-1">{typeInfo.label}</span>
              </Badge>
              <Badge variant="outline" className="gap-1">
                {draft.platform === 'anam' ? (
                  <><Video className="w-3 h-3" /> Video</>
                ) : (
                  <><Volume2 className="w-3 h-3" /> Audio</>
                )}
              </Badge>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">Step {draft.current_step} of {TOTAL_STEPS}</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Timestamps */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>Updated {lastUpdated}</span>
          </div>
          <div>
            <span>Expires in {expiresIn}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={handleContinue} className="flex-1">
            <Edit className="w-4 h-4 mr-2" />
            Continue Editing
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="icon" disabled={isDeleting}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Draft?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete "{draft.name || 'Untitled Draft'}". This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
